import {FC, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Button} from '../../../../components/Button/Button'
import Swal from 'sweetalert2'
import {POST} from '../../../../services/api'
import {Input} from '../../../../components/Input/Input'
import {Loading} from '../../../../components/Loading/Loading'
import {useNavigate} from 'react-router-dom'
import {i18n} from '../../../../translate/i18n'
import {BreadCrumbs} from '../../../../components/BreadCrumbs/breadCrumbrs'
import {hhmmToIsoToday, hhmmToSeconds} from './hooks/dateHour'

const CriarGradesHotelContent: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [title, setTitle] = useState('')
  const [hourInit, setHourInit] = useState<string | null>(null)
  const [hourEnd, setHourEnd] = useState<string>('')
  const [interval, setInterval] = useState<string>('')
  const [variation, setVariation] = useState('')
  const [status, setStatus] = useState<boolean>(true)
  const [list, setList] = useState<string[]>([])

  const addList = () => {
    if (!variation?.trim()) return
    setList((prev) => [...prev, variation.trim()]) // imutável
    setVariation('')
  }

  const removeList = (variationPosition: number) => {
    setList((prev) => prev.filter((_, index) => variationPosition !== index))
  }

  const createGrid = async () => {
    try {
      if (!title || list.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: i18n.t(`service_grid.missing_fields`) as string,
          text: i18n.t(`service_grid.missing_fields_message`) as string,
        })
        return
      }

      const payload = {
        title,
        content: {config: list},
        status,
        startHour: hhmmToIsoToday(hourInit),
        endHour: hhmmToIsoToday(hourEnd),
        interval: hhmmToSeconds(interval),
      }

      setLoading(true)
      const gridRes = await POST('/grid-hotel', payload)
      setLoading(false)

      if (gridRes.success === true) {
        Swal.fire({
          icon: 'success',
          title: i18n.t(`service_grid.grid_created`) as string,
          text: i18n.t(`service_grid.grid_created_message`) as string,
        }).then(() => navigate('/hospitality/structured-content/grid-hotel'))
      } else {
        Swal.fire({
          icon: 'error',
          title: i18n.t(`service_grid.error`) as string,
          text: gridRes?.message || (i18n.t(`service_grid.error_create_message`) as string),
        })
      }
    } catch (e) {
      setLoading(false)
      console.log(e)
      Swal.fire({
        icon: 'error',
        title: i18n.t(`service_grid.error`) as string,
        text: i18n.t(`service_grid.error_create_message`) as string,
      })
    }
  }

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t(`service_grid.create_service_grid`)}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t(`service_grid.fill_information_create`)}
                </span>
              </h3>
            </div>
            <div className='card-body'>
              <div className='card-body border-bottom'>
                <div className='d-flex justify-content-between align-items-center gap-5 flex-wrap'>
                  <div className='flex-grow-1'>
                    <Input
                      inputAttr={{type: 'string'}}
                      placeholder={i18n.t(`service_grid.service_grid_name_placeholder`)}
                      label={i18n.t(`service_grid.service_grid_name`)}
                      change={(value: string) => setTitle(value)}
                      value={title}
                      onlyLetters={true}
                      marginTop='0'
                      maxCaracteres={30}
                    />
                  </div>
                  <div className='flex-grow-1'>
                    <Input
                      inputAttr={{type: 'select'}}
                      value={status}
                      change={(val: boolean) => setStatus(val)}
                      label={i18n.t(`general.active`)}
                      options={[
                        {select: i18n.t(`general.active`), value: true},
                        {select: i18n.t(`general.inactive`), value: false},
                      ]}
                      marginTop='0'
                    />
                  </div>
                </div>

                <div className='d-flex align-items-center gap-5 flex-wrap'>
                  <Input
                    inputAttr={{type: 'hour'}}
                    placeholder={i18n.t(`service_grid.start_hour_placeholder`)}
                    label={i18n.t(`service_grid.start_hour`)}
                    change={(value: string) => setHourInit(value)}
                    value={hourInit ?? ''}
                    marginTop='0'
                  />
                  <Input
                    inputAttr={{type: 'hour'}}
                    placeholder={i18n.t(`service_grid.end_hour_placeholder`)}
                    label={i18n.t(`service_grid.end_hour`)}
                    change={(value: string) => setHourEnd(value)}
                    value={hourEnd}
                    marginTop='0'
                  />
                  <Input
                    inputAttr={{type: 'hour'}}
                    placeholder={i18n.t(`service_grid.interval_placeholder`)}
                    label={i18n.t(`service_grid.interval`)}
                    change={(value: string) => setInterval(value)}
                    value={interval}
                    toolTip={true}
                    toolTipIcon={'info-circle'}
                    toolTiptext={i18n.t(`service_grid.interval_tooltip`)}
                    marginTop='0'
                  />
                </div>

                <div className='form-group mt-5'>
                  <label className='form-label fw-normal'>
                    {i18n.t(`service_grid.service_list`)}
                  </label>
                  <div className='input-group'>
                    <input
                      type='text'
                      className='form-control fw-normal'
                      placeholder={i18n.t(`service_grid.service_placeholder`)}
                      value={variation}
                      onChange={(event) => setVariation(event.target.value)}
                    />
                    <div className='input-group-append'>
                      <button
                        className='btn btn-success'
                        style={{borderTopLeftRadius: 0, borderBottomLeftRadius: 0}}
                        type='button'
                        onClick={addList}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>

                {list.map((variationListed, index) => (
                  <div className='form-group mt-5' key={index}>
                    <div className='input-group'>
                      <input
                        type='text'
                        className='form-control fw-normal'
                        placeholder={i18n.t(`service_grid.service_placeholder`)}
                        value={variationListed}
                        onChange={(event) => {
                          const newList = [...list]
                          newList[index] = event.target.value
                          setList(newList)
                        }}
                      />
                      <div className='input-group-append'>
                        <button
                          className='btn btn-danger'
                          style={{borderTopLeftRadius: 0, borderBottomLeftRadius: 0}}
                          type='button'
                          onClick={() => removeList(index)}
                        >
                          -
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className='d-flex w-100'>
                <div className='col-6 d-flex justify-content-start'>
                  <Button
                    text={i18n.t(`buttons.back`)}
                    color='info'
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{type: 'button'}}
                    click={() => navigate(-1)}
                    btnClass='fw-bolder m-9'
                  />
                </div>
                <div className='col-6  d-flex justify-content-end'>
                  <Button
                    text={i18n.t(`buttons.save`)}
                    color='primary'
                    size='small'
                    horizontalPadding={6}
                    btnAttr={{type: 'button'}}
                    click={createGrid}
                    btnClass='fw-bolder m-9'
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const CriarGradesHotel: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t(`service_grid.dashboard`) as string, path: 'dashboard', isActive: true},
          {title: i18n.t(`service_grid.service_grid`) as string, path: 'grid', isActive: true},
          {title: i18n.t(`buttons.create`) as string, path: 'grid/criar', isActive: true},
        ]}
      >
        {i18n.t(`service_grid.service_grid`)}
      </PageTitle>
      <CriarGradesHotelContent />
    </>
  )
}

export {CriarGradesHotel}
